import React, { useEffect, useState, useMemo, useCallback } from "react";
import axiosInstance from "../../api/axios";
import { Spinner, Pagination, Badge } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import Header from "../../layouts/Header";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft, Search, Box, GeoAlt, PersonBadge, Calendar3,
    ArrowClockwise, FileEarmarkSpreadsheet, XCircle, CheckCircle,
    HourglassSplit, ExclamationTriangle, Boxes,
} from "react-bootstrap-icons";

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const CSS = `
  :root {
    --lp-bg:#f0f2f5; --lp-surface:#fff; --lp-border:#e2e6ea; --lp-text:#1a202c;
    --lp-muted:#41505F; --lp-accent:#2563eb; --lp-accent-light:#eff6ff; --lp-accent-dark:#1d4ed8;
    --lp-success:#059669; --lp-success-light:#ecfdf5; --lp-warning:#d97706; --lp-warning-light:#fffbeb;
    --lp-danger:#dc2626; --lp-danger-light:#fef2f2; --lp-info:#0891b2; --lp-info-light:#ecfeff;
    --lp-shadow-sm:0 1px 3px rgba(0,0,0,.08),0 1px 2px rgba(0,0,0,.05);
    --lp-shadow-md:0 4px 12px rgba(0,0,0,.09),0 2px 4px rgba(0,0,0,.06);
    --lp-shadow-lg:0 10px 40px rgba(0,0,0,.12);
    --lp-radius:10px; --lp-radius-sm:8px; --lp-radius-xs:6px;
  }
  .lp-card{background:var(--lp-surface);border-radius:var(--lp-radius);box-shadow:var(--lp-shadow-md);border:1px solid var(--lp-border);overflow:hidden;}

  .lp-header{display:flex;justify-content:space-between;align-items:center;padding:18px 28px;border-bottom:1px solid var(--lp-border);gap:12px;}
  .lp-header-title{font-size:18px;font-weight:700;color:var(--lp-text);letter-spacing:-.3px;display:flex;align-items:center;gap:10px;}
  .lp-back-btn{display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:var(--lp-radius-xs);border:1.5px solid var(--lp-border);background:var(--lp-surface);color:var(--lp-muted);font-size:13px;font-weight:500;cursor:pointer;transition:all .18s;}
  .lp-back-btn:hover{border-color:var(--lp-accent);color:var(--lp-accent);background:var(--lp-accent-light);}

  .lp-filters{padding:20px 28px;border-bottom:1px solid var(--lp-border);background:#fafbfc;}
  .lp-filter-label{font-size:11.5px;font-weight:600;color:var(--lp-muted);letter-spacing:.6px;margin-bottom:5px;display:flex;align-items:center;gap:5px;text-transform:uppercase;}
  .lp-filter-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:16px;align-items:end;}
  .lp-filter-col{display:flex;flex-direction:column;}
  .lp-filters .react-datepicker-wrapper{width:100%;}
  .lp-filters .react-datepicker-wrapper input{width:100%;padding:8px 10px;border:1.5px solid #e2e6ea;border-radius:6px;font-size:13.5px;outline:none;background:#fff;height:38px;color:var(--lp-text);transition:border-color .18s,box-shadow .18s;}
  .lp-filters .react-datepicker-wrapper input:focus{border-color:var(--lp-accent);box-shadow:0 0 0 3px rgba(37,99,235,.10);}

  .lp-filter-bottom{display:flex;align-items:center;flex-wrap:wrap;gap:12px;min-height:42px;padding-top:4px;}
  .lp-generate-btn{height:38px;padding:0 20px;border-radius:var(--lp-radius-xs);background:var(--lp-accent);border:none;color:#fff;font-size:13.5px;font-weight:600;cursor:pointer;transition:background .18s,transform .12s;display:inline-flex;align-items:center;gap:7px;white-space:nowrap;}
  .lp-generate-btn:hover:not(:disabled){background:var(--lp-accent-dark);transform:translateY(-1px);}
  .lp-generate-btn:disabled{opacity:.55;cursor:not-allowed;}
  .lp-reset-btn{height:38px;padding:0 16px;border-radius:var(--lp-radius-xs);background:var(--lp-surface);border:1.5px solid var(--lp-border);color:var(--lp-muted);font-size:13px;font-weight:500;cursor:pointer;transition:all .18s;display:inline-flex;align-items:center;gap:6px;}
  .lp-reset-btn:hover{border-color:var(--lp-danger);color:var(--lp-danger);background:var(--lp-danger-light);}

  /* KPI */
  .lp-kpi-strip{display:grid;grid-template-columns:repeat(6,1fr);gap:0;border-radius:var(--lp-radius);border:1px solid var(--lp-border);overflow:hidden;box-shadow:var(--lp-shadow-sm);margin:20px 28px 0;}
  .lp-kpi-cell{padding:16px 18px;border-right:1px solid var(--lp-border);text-align:center;background:var(--lp-surface);transition:background .15s;}
  .lp-kpi-cell:last-child{border-right:none;}
  .lp-kpi-cell:hover{background:#fafbfc;}
  .lp-kpi-label{font-size:10.5px;font-weight:600;color:var(--lp-muted);text-transform:uppercase;letter-spacing:.6px;margin-bottom:6px;}
  .lp-kpi-value{font-size:19px;font-weight:700;letter-spacing:-.5px;}
  .lp-kpi-value.blue{color:var(--lp-accent);}.lp-kpi-value.green{color:var(--lp-success);}
  .lp-kpi-value.amber{color:var(--lp-warning);}.lp-kpi-value.red{color:var(--lp-danger);}
  .lp-kpi-value.ink{color:var(--lp-text);}.lp-kpi-value.teal{color:var(--lp-info);}
  .lp-kpi-sub{font-size:10.5px;color:var(--lp-muted);margin-top:4px;}

  /* Storage location breakdown */
  .lp-breakdown-wrap{margin:20px 28px 0;}
  .lp-breakdown-title{display:flex;align-items:center;gap:7px;font-size:13px;font-weight:700;color:var(--lp-text);margin-bottom:10px;}
  .lp-breakdown-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:12px;}
  .lp-loc-card{background:var(--lp-surface);border:1px solid var(--lp-border);border-radius:var(--lp-radius-sm);padding:14px 16px;box-shadow:var(--lp-shadow-sm);transition:box-shadow .15s,border-color .15s;}
  .lp-loc-card:hover{box-shadow:var(--lp-shadow-md);border-color:#cbd5e1;}
  .lp-loc-card-head{display:flex;align-items:center;gap:8px;margin-bottom:10px;}
  .lp-loc-icon{width:30px;height:30px;border-radius:8px;background:var(--lp-accent-light);color:var(--lp-accent);display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .lp-loc-name{font-size:13px;font-weight:700;color:var(--lp-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .lp-loc-sub{font-size:10.5px;color:var(--lp-muted);}
  .lp-loc-stats{display:flex;justify-content:space-between;gap:8px;padding-top:8px;border-top:1px solid var(--lp-border);}
  .lp-loc-stat{text-align:center;flex:1;}
  .lp-loc-stat-val{font-size:14px;font-weight:700;}
  .lp-loc-stat-lbl{font-size:9.5px;color:var(--lp-muted);text-transform:uppercase;letter-spacing:.5px;margin-top:2px;}

  /* Table */
  .lp-toolbar{display:flex;justify-content:space-between;align-items:center;padding:16px 28px;gap:12px;flex-wrap:wrap;border-bottom:1px solid var(--lp-border);}
  .lp-toolbar-left{display:flex;align-items:center;gap:8px;flex:1;}
  .lp-toolbar-right{display:flex;align-items:center;gap:8px;}
  .lp-search-wrap{position:relative;flex:1;max-width:360px;}
  .lp-search-wrap .search-icon{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--lp-muted);pointer-events:none;font-size:14px;}
  .lp-search-input{width:100%;padding:9px 12px 9px 36px;border:1.5px solid var(--lp-border);border-radius:var(--lp-radius-xs);font-size:13.5px;background:var(--lp-surface);color:var(--lp-text);transition:border-color .18s,box-shadow .18s;outline:none;height:38px;}
  .lp-search-input:focus{border-color:var(--lp-accent);box-shadow:0 0 0 3px rgba(37,99,235,.10);}
  .lp-toolbar-btn{height:38px;padding:0 14px;border-radius:var(--lp-radius-xs);border:1.5px solid var(--lp-border);background:var(--lp-surface);font-size:12.5px;font-weight:500;color:var(--lp-muted);cursor:pointer;display:inline-flex;align-items:center;gap:6px;transition:all .18s;white-space:nowrap;}
  .lp-toolbar-btn:hover:not(:disabled){border-color:var(--lp-accent);color:var(--lp-accent);background:var(--lp-accent-light);}
  .lp-toolbar-btn:disabled{opacity:.5;cursor:not-allowed;}
  .lp-per-page{display:flex;align-items:center;gap:8px;font-size:12.5px;color:var(--lp-muted);}
  .lp-per-page select{padding:5px 10px;border:1.5px solid var(--lp-border);border-radius:var(--lp-radius-xs);font-size:12.5px;background:var(--lp-surface);color:var(--lp-text);cursor:pointer;outline:none;}

  .lp-table-wrap{overflow-x:auto;margin:0 28px 20px;border-radius:var(--lp-radius-sm);border:1px solid var(--lp-border);box-shadow:var(--lp-shadow-sm);}
  .lp-table{width:100%;border-collapse:collapse;font-size:13px;}
  .lp-table thead th{padding:11px 14px;background:#f8fafc;border-bottom:1px solid var(--lp-border);font-size:11px;font-weight:700;color:var(--lp-muted);text-transform:uppercase;letter-spacing:.6px;white-space:nowrap;}
  .lp-table tbody tr{border-bottom:1px solid #f0f2f5;transition:background .12s;}
  .lp-table tbody tr:last-child{border-bottom:none;}
  .lp-table tbody tr:hover{background:#f8fafc;}
  .lp-table td{padding:11px 14px;vertical-align:middle;color:var(--lp-text);}
  .lp-material-name{font-weight:600;font-size:13px;color:var(--lp-text);}
  .lp-material-cat{font-size:11px;color:var(--lp-muted);margin-top:2px;}
  .lp-loc-pill{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:20px;font-size:11.5px;font-weight:600;background:var(--lp-info-light);color:var(--lp-info);white-space:nowrap;}
  .lp-amount{font-size:12.5px;font-weight:600;white-space:nowrap;}
  .lp-amount.green{color:var(--lp-success);}.lp-amount.amber{color:var(--lp-warning);}
  .lp-amount.red{color:var(--lp-danger);}.lp-amount.ink{color:var(--lp-text);}
  .lp-unit{font-size:10.5px;color:var(--lp-muted);font-weight:400;margin-left:3px;}

  .lp-badge{display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:20px;font-size:11px;font-weight:700;letter-spacing:.2px;white-space:nowrap;}
  .lp-badge.completed{background:var(--lp-success-light);color:var(--lp-success);}
  .lp-badge.partial{background:var(--lp-warning-light);color:var(--lp-warning);}
  .lp-badge.pending{background:var(--lp-danger-light);color:var(--lp-danger);}

  .lp-pagination{display:flex;justify-content:space-between;align-items:center;padding:14px 28px 20px;flex-wrap:wrap;gap:12px;}
  .lp-page-info{font-size:12.5px;color:var(--lp-muted);}

  .lp-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:72px 24px;color:var(--lp-muted);text-align:center;}
  .lp-empty-icon{font-size:44px;margin-bottom:14px;opacity:.4;}
  .lp-empty-title{font-size:16px;font-weight:600;color:#374151;margin-bottom:6px;}
  .lp-empty-sub{font-size:13px;color:var(--lp-muted);max-width:340px;}
  .lp-loading{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px;gap:14px;color:var(--lp-muted);}

  @media(max-width:1200px){
    .lp-filter-grid{grid-template-columns:repeat(2,1fr);}
    .lp-kpi-strip{grid-template-columns:repeat(3,1fr);}
  }
  @media(max-width:768px){
    .lp-filter-grid{grid-template-columns:1fr;}
    .lp-kpi-strip{grid-template-columns:repeat(2,1fr);}
  }
`;

/* ─────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────── */
const getAccessToken = () => localStorage.getItem("authToken");

const fmtDate = (d) => {
    if (!d) return undefined;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtViewDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const fmtViewTime = (d) =>
    new Date(d).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });

const fmtQty = (n) => {
    const num = Number(n) || 0;
    return Number.isInteger(num) ? num.toLocaleString("en-IN") : num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const getPickStatus = (row) => {
    const requested = Number(row?.requestedQuantity) || 0;
    const picked = Number(row?.pickedUpQuantity) || 0;
    const balance = Number(row?.balanceQuantity) || 0;
    if (balance === 0 && picked > 0) return "completed";
    if (picked > 0 && balance > 0) return "partial";
    return "pending";
};
const statusLabel = { completed: "Completed", partial: "Partial", pending: "Pending" };
const statusIcon = {
    completed: <CheckCircle size={11} />,
    partial: <HourglassSplit size={11} />,
    pending: <ExclamationTriangle size={11} />,
};

const selectStyles = {
    control: (p, s) => ({
        ...p,
        borderRadius: 6,
        minHeight: 38,
        height: 38,
        borderColor: s.isFocused ? "#2563eb" : "#e2e6ea",
        boxShadow: s.isFocused ? "0 0 0 3px rgba(37,99,235,.1)" : "none",
        "&:hover": { borderColor: s.isFocused ? "#2563eb" : "#e2e6ea" },
        fontSize: 13.5,
    }),
    valueContainer: (p) => ({ ...p, padding: "0 8px", height: 36 }),
    indicatorsContainer: (p) => ({ ...p, height: 36 }),
    placeholder: (p) => ({ ...p, color: "#9ca3af" }),
    option: (p, s) => ({
        ...p,
        fontSize: 13.5,
        background: s.isSelected ? "#2563eb" : s.isFocused ? "#eff6ff" : "#fff",
    }),
};

/* ─────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────── */
const LogisticsPickupReport = () => {
    const navigate = useNavigate();
    const accessToken = getAccessToken();

    /* ── Dropdown data ── */
    const [storageLocations, setStorageLocations] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [loadingMaterials, setLoadingMaterials] = useState(false);
    const [usersMap, setUsersMap] = useState({});

    /* ── Filters ── */
    const [selectedStorageLocation, setSelectedStorageLocation] = useState(null);
    const [selectedRawMaterial, setSelectedRawMaterial] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [searchText, setSearchText] = useState("");

    /* ── Table / data ── */
    const [logs, setLogs] = useState([]);
    const [paginationData, setPaginationData] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 50 });
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(50);

    /* ── UI ── */
    const [loading, setLoading] = useState(false);
    const [subBtnLoading, setSubBtnLoading] = useState(false);
    const [clicked, setClicked] = useState(false);
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    /* ─── Init ─── */
    useEffect(() => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        setStartDate(today);
        setEndDate(today);
        fetchStorageLocations();
        fetchRawMaterials();
        fetchAdminUsers();
    }, []);

    useEffect(() => {
        if (clicked) fetchLogisticsReport(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordsPerPage]);

    /* ═══════════════════════════════════════
       DROPDOWN FETCHERS
    ═══════════════════════════════════════ */
    const fetchStorageLocations = async () => {
        setLoadingLocations(true);
        try {
            const headers = { Authorization: `Bearer ${getAccessToken()}` };
            const response = await axiosInstance.get("storage-location/getCurrentStock", { headers });
            if (response?.data?.data?.storageLocations) {
                setStorageLocations(response.data.data.storageLocations);
            }
        } catch (err) {
            // silently ignore; the select will just show no options
        } finally {
            setLoadingLocations(false);
        }
    };

    const fetchRawMaterials = async () => {
        setLoadingMaterials(true);
        try {
            const headers = { Authorization: `Bearer ${getAccessToken()}` };
            const response = await axiosInstance.get("raw-material", { headers });
            const payload = response?.data?.data;
            const list = Array.isArray(payload)
                ? payload
                : payload?.rawMaterials || payload?.materials || payload?.data || [];
            setRawMaterials(Array.isArray(list) ? list : []);
        } catch (err) {
            // silently ignore; the select will just show no options
        } finally {
            setLoadingMaterials(false);
        }
    };

    const fetchAdminUsers = async () => {
        try {
            const headers = { Authorization: `Bearer ${getAccessToken()}` };
            const res = await axiosInstance.get("admin-user-management", { headers });
            const list = res?.data?.data?.dbResponse || [];
            const map = {};
            list.forEach((u) => { map[u._id] = u.name; });
            setUsersMap(map);
        } catch (err) {
            // silently ignore; user column will show "—"
        }
    };

    const storageLocationOptions = useMemo(
        () =>
            (storageLocations || []).map((loc) => ({
                value: loc._id || loc.id,
                label: loc.name,
            })),
        [storageLocations]
    );

    const rawMaterialOptions = useMemo(
        () =>
            (rawMaterials || []).map((m) => ({
                value: m._id || m.id,
                label: m.unit ? `${m.name} · ${m.unit}` : m.name,
            })),
        [rawMaterials]
    );

    /* ═══════════════════════════════════════
       MAIN REPORT FETCH
    ═══════════════════════════════════════ */
    const fetchLogisticsReport = async (page = 1) => {
        try {
            setLoading(true);
            const headers = { Authorization: `Bearer ${getAccessToken()}` };
            const params = {};
            if (selectedStorageLocation) params.storageLocation = selectedStorageLocation.value;
            if (selectedRawMaterial) params.rawMaterial = selectedRawMaterial.value;
            if (startDate) params.startDate = fmtDate(startDate);
            if (endDate) params.endDate = fmtDate(endDate);
            params.page = String(page);
            params.limit = String(recordsPerPage);

            const res = await axiosInstance.get(
                "stock-requisition/fetch-logistic-pickup-log-report",
                { headers, params }
            );

            const body = res?.data;
            if (body?.status) {
                const rows = body.data?.data || [];
                const pg = body.data?.pagination || {
                    currentPage: 1, totalPages: 1, totalRecords: rows.length, limit: recordsPerPage,
                };
                setLogs(rows);
                setPaginationData(pg);
                setCurrentPage(pg.currentPage || 1);
                if (rows.length === 0) {
                    toast.info("No pickup records found for the selected filters.", {
                        position: "top-right", autoClose: 3000, theme: "light",
                    });
                } else {
                    toast.success("Report fetched successfully", {
                        position: "top-right", autoClose: 2000, theme: "light",
                    });
                }
            } else {
                setLogs([]);
                toast.info("No records found.", { position: "top-right", autoClose: 3000, theme: "light" });
            }
        } catch (error) {
            setLogs([]);
            const s = error?.response?.status;
            const msg =
                s === 406
                    ? error.response.data?.error?.message || error.response.data?.error
                    : s === 412
                        ? error.response.data?.data?.message
                        : s === 500
                            ? error.response.data?.error
                            : "Something went wrong while fetching the report.";
            toast.error(msg, { position: "top-right", autoClose: 3000, theme: "light" });
        } finally {
            setLoading(false);
            setSubBtnLoading(false);
        }
    };

    const handleSubmit = () => {
        setSubBtnLoading(true);
        setClicked(true);
        fetchLogisticsReport(1);
    };

    const handleReset = () => {
        setSelectedStorageLocation(null);
        setSelectedRawMaterial(null);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        setStartDate(today);
        setEndDate(today);
        setSearchText("");
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > paginationData.totalPages) return;
        fetchLogisticsReport(page);
    };

    /* ═══════════════════════════════════════
       DERIVED DATA — KPIs, breakdown, filtering
    ═══════════════════════════════════════ */
    const kpis = useMemo(() => {
        const totalLogs = logs.length;
        let totalRequested = 0, totalPicked = 0, totalBalance = 0;
        let completed = 0, partial = 0, pending = 0;
        const locations = new Set();
        const materials = new Set();

        logs.forEach((row) => {
            totalRequested += Number(row.requestedQuantity) || 0;
            totalPicked += Number(row.pickedUpQuantity) || 0;
            totalBalance += Number(row.balanceQuantity) || 0;
            const status = getPickStatus(row);
            if (status === "completed") completed += 1;
            else if (status === "partial") partial += 1;
            else pending += 1;
            if (row.storageLocation?.name) locations.add(row.storageLocation.name);
            if (row.rawMaterial?.name) materials.add(row.rawMaterial.name);
        });

        return {
            totalLogs, totalRequested, totalPicked, totalBalance,
            completed, partial, pending,
            locationCount: locations.size, materialCount: materials.size,
        };
    }, [logs]);

    const locationBreakdown = useMemo(() => {
        const map = {};
        logs.forEach((row) => {
            const key = row.storageLocation?.name || "Unassigned";
            if (!map[key]) {
                map[key] = { name: key, requested: 0, picked: 0, balance: 0, count: 0 };
            }
            map[key].requested += Number(row.requestedQuantity) || 0;
            map[key].picked += Number(row.pickedUpQuantity) || 0;
            map[key].balance += Number(row.balanceQuantity) || 0;
            map[key].count += 1;
        });
        return Object.values(map).sort((a, b) => b.picked - a.picked);
    }, [logs]);

    const filteredLogs = useMemo(() => {
        if (!searchText.trim()) return logs;
        const q = searchText.trim().toLowerCase();
        return logs.filter((row) => {
            const matName = row.rawMaterial?.name?.toLowerCase() || "";
            const matCat = row.rawMaterial?.mainCategoryName?.toLowerCase() || "";
            const matSubCat = row.rawMaterial?.subCategoryName?.toLowerCase() || "";
            const locName = row.storageLocation?.name?.toLowerCase() || "";
            const userName = (usersMap[row.user] || "").toLowerCase();
            return (
                matName.includes(q) ||
                matCat.includes(q) ||
                matSubCat.includes(q) ||
                locName.includes(q) ||
                userName.includes(q)
            );
        });
    }, [logs, searchText, usersMap]);

    /* ═══════════════════════════════════════
       DOWNLOAD EXCEL — backend-generated (download:true)
    ═══════════════════════════════════════ */
    const handleDownloadExcel = async () => {
        try {
            setDownloadBtnLoading(true);
            const headers = { Authorization: `Bearer ${getAccessToken()}` };
            const params = {};
            if (selectedStorageLocation) params.storageLocation = selectedStorageLocation.value;
            if (selectedRawMaterial) params.rawMaterial = selectedRawMaterial.value;
            if (startDate) params.startDate = fmtDate(startDate);
            if (endDate) params.endDate = fmtDate(endDate);
            params.download = true;

            const response = await axiosInstance.get(
                "stock-requisition/fetch-logistic-pickup-log-report",
                { headers, params, responseType: "blob" }
            );

            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `Logistics_Pickup_Report_${fmtDate(startDate) || "all"}_to_${fmtDate(endDate) || "all"}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);

            toast.success("Excel downloaded successfully!", {
                position: "top-right", autoClose: 3000, theme: "light",
            });
        } catch (error) {
            const s = error?.response?.status;
            const msg =
                s === 406
                    ? error.response.data?.error?.message || error.response.data?.error
                    : "Failed to download Excel.";
            toast.error(msg, { position: "top-right", autoClose: 3000, theme: "light" });
        } finally {
            setDownloadBtnLoading(false);
        }
    };

    /* ─── Pagination items ─── */
    const renderPaginationItems = () => {
        const total = paginationData.totalPages;
        if (total <= 1) return null;
        const items = [];
        if (total > 15) {
            if (currentPage > 3)
                items.push(<Pagination.Item key="1" onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (currentPage > 4) items.push(<Pagination.Ellipsis key="el1" />);
            for (let i = 0; i < 5; i++) {
                const p = currentPage > 3 ? currentPage - 2 + i : i + 1;
                if (p <= total)
                    items.push(
                        <Pagination.Item key={p} active={p === currentPage} onClick={() => handlePageChange(p)}>
                            {p}
                        </Pagination.Item>
                    );
            }
            if (currentPage < total - 3) items.push(<Pagination.Ellipsis key="el2" />);
            if (currentPage < total - 2)
                items.push(<Pagination.Item key={total} onClick={() => handlePageChange(total)}>{total}</Pagination.Item>);
        } else {
            for (let i = 1; i <= total; i++)
                items.push(
                    <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
                        {i}
                    </Pagination.Item>
                );
        }
        return items;
    };

    /* ════════════════════════════════════════════
       RENDER
    ════════════════════════════════════════════ */
    return (
        <React.Fragment>
            <style>{CSS}</style>
            <Header />
            <div className="main main-app" style={{ background: "#f0f2f5", minHeight: "100vh", padding: "24px" }}>
                <div className="lp-card">

                    {/* ── Page Header ── */}
                    <div className="lp-header">
                        <div className="lp-header-title">
                            <Boxes size={18} /> LOGISTICS PICKUP LOG REPORT
                        </div>
                        <button className="lp-back-btn" onClick={() => navigate("/reports/all-reports")}>
                            <ArrowLeft size={14} /> Back to Reports
                        </button>
                    </div>

                    {/* ════════════════════════════════════
              FILTERS
          ════════════════════════════════════ */}
                    <div className="lp-filters">
                        <div className="lp-filter-grid">

                            {/* Storage Location */}
                            <div className="lp-filter-col">
                                <label className="lp-filter-label"><GeoAlt size={12} /> Storage Location</label>
                                <Select
                                    options={storageLocationOptions}
                                    value={selectedStorageLocation}
                                    onChange={setSelectedStorageLocation}
                                    isLoading={loadingLocations}
                                    isClearable
                                    styles={selectStyles}
                                    placeholder="All locations…"
                                />
                            </div>

                            {/* Raw Material */}
                            <div className="lp-filter-col">
                                <label className="lp-filter-label"><Box size={12} /> Raw Material</label>
                                <Select
                                    options={rawMaterialOptions}
                                    value={selectedRawMaterial}
                                    onChange={setSelectedRawMaterial}
                                    isLoading={loadingMaterials}
                                    isClearable
                                    styles={selectStyles}
                                    placeholder="All materials…"
                                />
                            </div>

                            {/* Start Date */}
                            <div className="lp-filter-col">
                                <label className="lp-filter-label"><Calendar3 size={12} /> Start Date</label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={setStartDate}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText="Start Date"
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    maxDate={new Date()}
                                />
                            </div>

                            {/* End Date */}
                            <div className="lp-filter-col">
                                <label className="lp-filter-label"><Calendar3 size={12} /> End Date</label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={setEndDate}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText="End Date"
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate}
                                    maxDate={new Date()}
                                />
                            </div>
                        </div>

                        <div className="lp-filter-bottom">
                            <button
                                className="lp-generate-btn"
                                onClick={handleSubmit}
                                disabled={subBtnLoading || !startDate || !endDate}
                            >
                                {subBtnLoading ? <Spinner size="sm" animation="border" /> : (<><Search size={13} /> Generate Report</>)}
                            </button>
                            <button className="lp-reset-btn" onClick={handleReset}>
                                <XCircle size={13} /> Reset Filters
                            </button>
                        </div>
                    </div>

                    {/* ══ REPORT ══ */}
                    {clicked && (
                        <>
                            {/* ── KPI Strip ── */}
                            <div className="lp-kpi-strip">
                                <div className="lp-kpi-cell">
                                    <div className="lp-kpi-label">Total Pickups</div>
                                    <div className="lp-kpi-value ink">{kpis.totalLogs}</div>
                                    <div className="lp-kpi-sub">{kpis.locationCount} location{kpis.locationCount !== 1 ? "s" : ""} · {kpis.materialCount} item{kpis.materialCount !== 1 ? "s" : ""}</div>
                                </div>
                                <div className="lp-kpi-cell">
                                    <div className="lp-kpi-label">Requested Qty</div>
                                    <div className="lp-kpi-value blue">{fmtQty(kpis.totalRequested)}</div>
                                </div>
                                <div className="lp-kpi-cell">
                                    <div className="lp-kpi-label">Picked Up Qty</div>
                                    <div className="lp-kpi-value green">{fmtQty(kpis.totalPicked)}</div>
                                </div>
                                <div className="lp-kpi-cell">
                                    <div className="lp-kpi-label">Balance Pending</div>
                                    <div className="lp-kpi-value red">{fmtQty(kpis.totalBalance)}</div>
                                </div>
                                <div className="lp-kpi-cell">
                                    <div className="lp-kpi-label">Completed</div>
                                    <div className="lp-kpi-value green">{kpis.completed}</div>
                                </div>
                                <div className="lp-kpi-cell">
                                    <div className="lp-kpi-label">Partial / Pending</div>
                                    <div className="lp-kpi-value amber">{kpis.partial + kpis.pending}</div>
                                </div>
                            </div>

                            {/* ── Storage Location Breakdown ── */}
                            {locationBreakdown.length > 0 && (
                                <div className="lp-breakdown-wrap">
                                    <div className="lp-breakdown-title">
                                        <GeoAlt size={15} /> Picked Up by Storage Location
                                    </div>
                                    <div className="lp-breakdown-grid">
                                        {locationBreakdown.map((loc) => (
                                            <div className="lp-loc-card" key={loc.name}>
                                                <div className="lp-loc-card-head">
                                                    <div className="lp-loc-icon"><GeoAlt size={14} /></div>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div className="lp-loc-name">{loc.name}</div>
                                                        <div className="lp-loc-sub">{loc.count} pickup{loc.count !== 1 ? "s" : ""}</div>
                                                    </div>
                                                </div>
                                                <div className="lp-loc-stats">
                                                    <div className="lp-loc-stat">
                                                        <div className="lp-loc-stat-val" style={{ color: "var(--lp-accent)" }}>{fmtQty(loc.requested)}</div>
                                                        <div className="lp-loc-stat-lbl">Requested</div>
                                                    </div>
                                                    <div className="lp-loc-stat">
                                                        <div className="lp-loc-stat-val" style={{ color: "var(--lp-success)" }}>{fmtQty(loc.picked)}</div>
                                                        <div className="lp-loc-stat-lbl">Picked</div>
                                                    </div>
                                                    <div className="lp-loc-stat">
                                                        <div className="lp-loc-stat-val" style={{ color: loc.balance > 0 ? "var(--lp-danger)" : "var(--lp-muted)" }}>{fmtQty(loc.balance)}</div>
                                                        <div className="lp-loc-stat-lbl">Balance</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* ── Toolbar ── */}
                            <div className="lp-toolbar" style={{ marginTop: 20 }}>
                                <div className="lp-toolbar-left">
                                    <div className="lp-search-wrap">
                                        <Search size={13} className="search-icon" />
                                        <input
                                            className="lp-search-input"
                                            placeholder="Search item, category, location, user…"
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="lp-toolbar-right">
                                    {filteredLogs.length > 0 && (
                                        <span className="lp-page-info">
                                            Page {currentPage} of {paginationData.totalPages} · {paginationData.totalRecords} total
                                        </span>
                                    )}
                                    <button
                                        className="lp-toolbar-btn"
                                        style={logs.length > 0 ? { borderColor: "#059669", color: "#059669", background: "#ecfdf5" } : {}}
                                        onClick={handleDownloadExcel}
                                        disabled={downloadBtnLoading || logs.length === 0}
                                        title="Download this report as Excel"
                                    >
                                        {downloadBtnLoading ? (
                                            <Spinner size="sm" animation="border" />
                                        ) : (
                                            <>
                                                <FileEarmarkSpreadsheet size={13} /> Download Excel
                                            </>
                                        )}
                                    </button>
                                    <button className="lp-toolbar-btn" onClick={() => fetchLogisticsReport(currentPage)} title="Refresh">
                                        <ArrowClockwise size={13} /> Refresh
                                    </button>
                                    <div className="lp-per-page">
                                        <span>Show</span>
                                        <select value={recordsPerPage} onChange={(e) => setRecordsPerPage(Number(e.target.value))}>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* ── Table ── */}
                            {loading ? (
                                <div className="lp-loading">
                                    <Spinner animation="border" variant="primary" />
                                    <span style={{ fontSize: 13.5 }}>Loading pickup logs…</span>
                                </div>
                            ) : (
                                <div className="lp-table-wrap">
                                    <table className="lp-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 44 }}>Sr</th>
                                                <th style={{ width: 130 }}>Date / Time</th>
                                                <th>Raw Material</th>
                                                <th style={{ width: 160 }}>Storage Location</th>
                                                <th style={{ width: 100, textAlign: "right" }}>Requested</th>
                                                <th style={{ width: 100, textAlign: "right" }}>Picked Up</th>
                                                <th style={{ width: 100, textAlign: "right" }}>Balance</th>
                                                <th style={{ width: 110, textAlign: "center" }}>Status</th>
                                                <th style={{ width: 140 }}>Picked By</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredLogs.length > 0 ? (
                                                filteredLogs.map((row, idx) => {
                                                    const status = getPickStatus(row);
                                                    return (
                                                        <tr key={row._id || idx}>
                                                            <td style={{ color: "#9ca3af", fontSize: 12 }}>
                                                                {(currentPage - 1) * recordsPerPage + idx + 1}
                                                            </td>
                                                            <td>
                                                                <div style={{ fontSize: 12.5, fontWeight: 500 }}>{fmtViewDate(row.date || row.createdAt)}</div>
                                                                <div style={{ fontSize: 11.5, color: "#9ca3af" }}>{fmtViewTime(row.date || row.createdAt)}</div>
                                                            </td>
                                                            <td>
                                                                <div className="lp-material-name">{row.rawMaterial?.name || "—"}</div>
                                                                <div className="lp-material-cat">
                                                                    {row.rawMaterial?.mainCategoryName}
                                                                    {row.rawMaterial?.subCategoryName ? ` · ${row.rawMaterial.subCategoryName}` : ""}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className="lp-loc-pill"><GeoAlt size={11} /> {row.storageLocation?.name || "Unassigned"}</span>
                                                            </td>
                                                            <td style={{ textAlign: "right" }}>
                                                                <span className="lp-amount ink">{fmtQty(row.requestedQuantity)}<span className="lp-unit">{row.rawMaterial?.unit}</span></span>
                                                            </td>
                                                            <td style={{ textAlign: "right" }}>
                                                                <span className="lp-amount green">{fmtQty(row.pickedUpQuantity)}<span className="lp-unit">{row.rawMaterial?.unit}</span></span>
                                                            </td>
                                                            <td style={{ textAlign: "right" }}>
                                                                <span className={`lp-amount ${row.balanceQuantity > 0 ? "red" : "ink"}`}>
                                                                    {fmtQty(row.balanceQuantity)}<span className="lp-unit">{row.rawMaterial?.unit}</span>
                                                                </span>
                                                            </td>
                                                            <td style={{ textAlign: "center" }}>
                                                                <span className={`lp-badge ${status}`}>{statusIcon[status]} {statusLabel[status]}</span>
                                                            </td>
                                                            <td>
                                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5 }}>
                                                                    <PersonBadge size={12} style={{ color: "#9ca3af" }} />
                                                                    {row.user ? row.user.name : "—"} <br />
                                                                    {row.user ? row?.user?.email : "-"}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={9}>
                                                        <div className="lp-empty" style={{ padding: "40px 24px" }}>
                                                            <div className="lp-empty-icon">📦</div>
                                                            <div className="lp-empty-title">No pickup logs found</div>
                                                            <div className="lp-empty-sub">Try adjusting your filters or search</div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* ── Pagination ── */}
                            {paginationData.totalPages > 1 && (
                                <div className="lp-pagination">
                                    <div className="lp-page-info">
                                        Showing {filteredLogs.length} of {logs.length} records on this page · Page {currentPage} of {paginationData.totalPages}
                                    </div>
                                    <Pagination className="mb-0" size="sm">
                                        <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />
                                        {renderPaginationItems()}
                                        <Pagination.Next disabled={currentPage === paginationData.totalPages} onClick={() => handlePageChange(currentPage + 1)} />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Initial empty state ── */}
                    {!clicked && (
                        <div className="lp-empty">
                            <div className="lp-empty-icon">🚚</div>
                            <div className="lp-empty-title">Select filters &amp; generate the report</div>
                            <div className="lp-empty-sub">
                                Optionally narrow down by storage location or raw material, pick a date range, and click Generate Report.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};

export default LogisticsPickupReport;